import React, { useEffect, useRef, useState } from "react";
import { useWeb3 } from "../context/Web3Context";
import { useUserProfile } from "../hooks/useContract";
import { bytes32ToString } from "../utils/format";
import Button from "../components/Common/Button";
import FullScreenLoader from "../components/Loader/FullScreenLoader";
import { PACKAGES, MATRIX_LEVELS } from "../config/constants";

function Matrix() {
  const { account, connect, getContract } = useWeb3();
  const { loading, isRegistered, user, packages } = useUserProfile();

  const [selectedPkg, setSelectedPkg] = useState(1);
  // Path: array of user IDs from user.id down to what we're currently viewing
  const [viewPath, setViewPath] = useState([]); // when empty = viewing self
  const [viewingUser, setViewingUser] = useState(null);
  const [tree, setTree] = useState({ l1: [null, null], l2: [[null, null], [null, null]] });
  const [matrixUpline, setMatrixUpline] = useState(null); // { id, name } of viewing user's matrix upline for selected pkg
  const [levels, setLevels] = useState({}); // {1: [ids], 2: [ids]...}
  const [treeLoading, setTreeLoading] = useState(true);
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [levelMembers, setLevelMembers] = useState([]);
  const [levelLoading, setLevelLoading] = useState(false);
  const [levelPage, setLevelPage] = useState(0);
  const MEMBERS_PER_PAGE = 10;

  const cancelRef = useRef(false);

  const currentViewId = viewPath.length > 0 ? viewPath[viewPath.length - 1] : user?.id;

  // Load tree + levels for the current view
  useEffect(() => {
    cancelRef.current = false;
    setExpandedLevel(null);
    setLevelMembers([]);
    setTreeLoading(true);
    const load = async () => {
      const contract = getContract();
      if (!contract || !currentViewId) {
        setTreeLoading(false);
        return;
      }
      try {
        // Fetch viewing user's info
        const rawUser = await contract.users(currentViewId);
        if (!cancelRef.current) {
          setViewingUser({
            id: currentViewId,
            name: bytes32ToString(rawUser.name),
            totalDirectReferrals: rawUser.totalDirectReferrals.toString(),
          });
        }

        // Fetch matrix upline for the current view (parent in the matrix for this package)
        try {
          const upId = await contract.packageMatrixUpline(selectedPkg, currentViewId);
          const upIdStr = upId.toString();
          if (upIdStr !== "0") {
            const upUser = await contract.users(upIdStr);
            if (!cancelRef.current) {
              setMatrixUpline({ id: upIdStr, name: bytes32ToString(upUser.name) });
            }
          } else {
            if (!cancelRef.current) setMatrixUpline(null);
          }
        } catch {
          if (!cancelRef.current) setMatrixUpline(null);
        }

        // Team size at each level
        const levelData = {};
        const totals = [0, 0, 0, 0, 0, 0, 0, 0];
        for (let l = 1; l <= MATRIX_LEVELS; l++) {
          const arr = await contract.getMatrixLevel(currentViewId, selectedPkg, l);
          const ids = arr.map((x) => x.toString());
          levelData[l] = ids;
          totals[l - 1] = ids.length;
        }
        if (!cancelRef.current) setLevels(levelData);

        // Build 2-level tree using getMatrixLevel for L1, then per-child L1 for L2
        const l1Ids = levelData[1] || [];
        const nodesL1 = [null, null];
        for (let i = 0; i < Math.min(2, l1Ids.length); i++) {
          try {
            const u = await contract.users(l1Ids[i]);
            nodesL1[i] = { id: l1Ids[i], name: bytes32ToString(u.name) };
          } catch { nodesL1[i] = { id: l1Ids[i], name: "" }; }
        }
        // For each L1 node, fetch its own L1 (which is our L2 relative to view)
        const nodesL2 = [[null, null], [null, null]];
        for (let i = 0; i < 2; i++) {
          if (!nodesL1[i]) continue;
          try {
            const children = await contract.getMatrixLevel(nodesL1[i].id, selectedPkg, 1);
            for (let j = 0; j < Math.min(2, children.length); j++) {
              try {
                const cu = await contract.users(children[j].toString());
                nodesL2[i][j] = { id: children[j].toString(), name: bytes32ToString(cu.name) };
              } catch { nodesL2[i][j] = { id: children[j].toString(), name: "" }; }
            }
          } catch { /* ignore */ }
        }
        if (!cancelRef.current) setTree({ l1: nodesL1, l2: nodesL2 });
      } catch {
        // ignore
      } finally {
        if (!cancelRef.current) setTreeLoading(false);
      }
    };
    load();
    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewId, selectedPkg]);

  // Reset page when a new level is opened
  useEffect(() => {
    setLevelPage(0);
  }, [expandedLevel]);

  // Load members for the current page of expanded level
  useEffect(() => {
    if (expandedLevel === null) return;
    const contract = getContract();
    if (!contract || !currentViewId) return;
    let cancelled = false;
    const load = async () => {
      setLevelLoading(true);
      try {
        const ids = levels[expandedLevel] || [];
        // Slice by page — 10 per page, no hard cap
        const start = levelPage * MEMBERS_PER_PAGE;
        const capped = ids.slice(start, start + MEMBERS_PER_PAGE);
        const details = await Promise.all(
          capped.map(async (id) => {
            try {
              const u = await contract.users(id);
              // matrix upline (parent in matrix)
              let matrixUpId = "0", matrixUpName = "";
              try {
                const mu = await contract.packageMatrixUpline(selectedPkg, id);
                matrixUpId = mu.toString();
                if (matrixUpId !== "0") {
                  const mun = await contract.users(matrixUpId);
                  matrixUpName = bytes32ToString(mun.name);
                }
              } catch {}
              // sponsor
              let sponsorId = u.referrerId.toString();
              let sponsorName = "";
              if (sponsorId !== "0") {
                try {
                  const sn = await contract.users(sponsorId);
                  sponsorName = bytes32ToString(sn.name);
                } catch {}
              }
              return {
                id,
                name: bytes32ToString(u.name),
                sponsorId,
                sponsorName,
                matrixUpId,
                matrixUpName,
              };
            } catch { return null; }
          })
        );
        if (!cancelled) setLevelMembers(details.filter(Boolean));
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLevelLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedLevel, currentViewId, selectedPkg, levelPage]);

  if (!account) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text mb-lg">Connect wallet.</div>
        <Button variant="primary" onClick={connect}>Connect Wallet</Button>
      </div>
    );
  }
  if (loading) return <FullScreenLoader text="Loading Matrix" />;
  if (!isRegistered) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text">Register first.</div>
      </div>
    );
  }

  const drillTo = (node) => {
    if (!node) return;
    setViewPath((p) => [...p, node.id]);
  };
  const backOne = () => setViewPath((p) => p.slice(0, -1));
  const resetToMe = () => setViewPath([]);
  const isSelfView = viewPath.length === 0;

  return (
    <div className="animate-fade-in-up">
      <div className="section-header">
        <div className="section-title">Matrix Explorer</div>
      </div>

      {/* Package selector */}
      <div style={{ marginBottom: 16 }}>
        <label className="input-label">Package</label>
        <select
          className="select"
          value={selectedPkg}
          onChange={(e) => { setSelectedPkg(Number(e.target.value)); setViewPath([]); }}
        >
          {PACKAGES.map((p) => {
            const isActive = packages[p.id - 1]?.isActive;
            return (
              <option key={p.id} value={p.id}>
                Package {p.id} — ${p.price}{isActive ? " ✓" : ""}
              </option>
            );
          })}
        </select>
      </div>

      {/* Breadcrumb / back */}
      {!isSelfView && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
          <Button variant="ghost" size="sm" onClick={backOne}>← Back</Button>
          <Button variant="secondary" size="sm" onClick={resetToMe}>◇ My Matrix</Button>
          <span className="badge badge-violet" style={{ marginLeft: "auto" }}>
            Viewing #{currentViewId}
          </span>
        </div>
      )}

      {/* Tree view */}
      <div className="matrix-tree">
        {treeLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <span className="spinner spinner-lg" />
          </div>
        ) : (
          <div className="matrix-tree-inner">
            {/* Matrix Upline (parent in matrix for selected package) */}
            {matrixUpline ? (
              <div className="matrix-slot" style={{ marginBottom: 4 }}>
                <div style={{
                  fontSize: 9, color: "var(--text-muted)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  marginBottom: 4, fontWeight: 600, textAlign: "center",
                }}>
                  Matrix Upline
                </div>
                <div className="matrix-node">
                  <div
                    className="matrix-node-circle"
                    style={{
                      background: "rgba(139, 92, 246, 0.12)",
                      borderColor: "rgba(139, 92, 246, 0.6)",
                      color: "var(--violet-bright)",
                      width: 44, height: 44, fontSize: 11,
                    }}
                  >
                    {matrixUpline.name ? matrixUpline.name[0]?.toUpperCase() : "◇"}
                  </div>
                  <div className="matrix-node-label" style={{ color: "var(--violet-bright)" }}>
                    #{matrixUpline.id}
                  </div>
                </div>
                {/* Small connector line down to root */}
                <div style={{ width: 1, height: 14, background: "var(--cyan-border)", margin: "2px 0" }} />
              </div>
            ) : null}

            {/* Root */}
            <div className="matrix-slot">
              <div className="matrix-node root">
                <div className="matrix-node-circle">
                  {viewingUser?.name ? viewingUser.name[0]?.toUpperCase() : "◇"}
                </div>
                <div className="matrix-node-label">#{currentViewId || "—"}</div>
              </div>
            </div>

            {/* Connector root→L1 */}
            <div className="matrix-conn c-root">
              <span className="drop" style={{ left: "25%" }} />
              <span className="drop" style={{ left: "75%" }} />
            </div>

            {/* Level 1 */}
            <div className="matrix-level lvl-1">
              {tree.l1.map((node, i) => (
                <div key={i} className="matrix-slot">
                  <div
                    className={`matrix-node ${!node ? "empty" : ""}`}
                    onClick={() => node && drillTo(node)}
                  >
                    <div className="matrix-node-circle">
                      {node ? (node.name ? node.name[0]?.toUpperCase() : "·") : "·"}
                    </div>
                    <div className="matrix-node-label">
                      {node ? `#${node.id}` : "empty"}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Connector L1→L2 (4 drops) */}
            <div className="matrix-conn c-l1">
              <span className="drop" style={{ left: "17%" }} />
              <span className="drop" style={{ left: "39%" }} />
              <span className="drop" style={{ left: "61%" }} />
              <span className="drop" style={{ left: "83%" }} />
            </div>

            {/* Level 2 (4 slots) */}
            <div className="matrix-level lvl-2">
              {tree.l2.flat().map((node, i) => (
                <div key={i} className="matrix-slot">
                  <div
                    className={`matrix-node small ${!node ? "empty" : ""}`}
                    onClick={() => node && drillTo(node)}
                  >
                    <div className="matrix-node-circle">
                      {node ? (node.name ? node.name[0]?.toUpperCase() : "·") : "·"}
                    </div>
                    <div className="matrix-node-label">
                      {node ? `#${node.id}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <p style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", marginBottom: 20 }}>
        Tap any downline to drill deeper. Uplines cannot be viewed.
      </p>

      {/* Levels list */}
      <div className="section-header">
        <div className="section-title">Level Breakdown</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((lvl) => {
          const ids = levels[lvl] || [];
          const required = 2 ** lvl;
          const pct = Math.min(100, (ids.length / required) * 100);
          const complete = ids.length >= required;
          const isOpen = expandedLevel === lvl;
          return (
            <div key={lvl} className="card">
              <div
                style={{ display: "flex", alignItems: "center", gap: 12, cursor: ids.length > 0 ? "pointer" : "default" }}
                onClick={() => {
                  if (ids.length > 0) setExpandedLevel(isOpen ? null : lvl);
                }}
              >
                <div className={`badge ${complete ? "badge-green" : "badge-cyan"}`} style={{ minWidth: 44, justifyContent: "center" }}>
                  L{lvl}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 600 }}>
                    {ids.length} / {required}
                  </div>
                  <div className="ceiling-bar" style={{ marginTop: 6, marginBottom: 0 }}>
                    <div className="ceiling-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: 14 }}>
                  {ids.length > 0 ? (isOpen ? "▲" : "▼") : ""}
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                  {levelLoading ? (
                    <div style={{ textAlign: "center", padding: 20 }}>
                      <span className="spinner" />
                    </div>
                  ) : levelMembers.length === 0 ? (
                    <div className="empty-state-text">No members</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {levelMembers.map((m) => (
                        <div key={m.id} style={{ background: "rgba(15,19,48,0.5)", border: "1px solid var(--border)", borderRadius: 10, padding: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div className="list-item-icon" style={{ width: 32, height: 32, fontSize: 12 }}>
                              {m.name ? m.name[0]?.toUpperCase() : "◇"}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                                {m.name || "—"}
                              </div>
                              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--cyan)" }}>
                                #{m.id}
                              </div>
                            </div>
                          </div>
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--border)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 11 }}>
                            <div>
                              <div style={{ color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.03em", fontSize: 9 }}>Sponsor</div>
                              <div style={{ color: "var(--text-secondary)" }}>
                                {m.sponsorName || "—"} <span style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>#{m.sponsorId}</span>
                              </div>
                            </div>
                            <div>
                              <div style={{ color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.03em", fontSize: 9 }}>Matrix Upline</div>
                              <div style={{ color: "var(--text-secondary)" }}>
                                {m.matrixUpName || "—"} <span style={{ color: "var(--violet-bright)", fontFamily: "var(--font-mono)" }}>#{m.matrixUpId}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(() => {
                        const total = levels[lvl]?.length || 0;
                        const totalPages = Math.max(1, Math.ceil(total / MEMBERS_PER_PAGE));
                        if (totalPages <= 1) return null;
                        const from = levelPage * MEMBERS_PER_PAGE + 1;
                        const to = Math.min(total, from + MEMBERS_PER_PAGE - 1);
                        return (
                          <div
                            style={{
                              marginTop: 10,
                              paddingTop: 10,
                              borderTop: "1px dashed var(--border)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                              {from}–{to} of {total}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={levelPage === 0 || levelLoading}
                                onClick={(e) => { e.stopPropagation(); setLevelPage((p) => Math.max(0, p - 1)); }}
                              >‹ Prev</Button>
                              <span style={{ fontSize: 11, color: "var(--text-secondary)", fontFamily: "var(--font-mono)", padding: "0 6px" }}>
                                {levelPage + 1} / {totalPages}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={levelPage >= totalPages - 1 || levelLoading}
                                onClick={(e) => { e.stopPropagation(); setLevelPage((p) => Math.min(totalPages - 1, p + 1)); }}
                              >Next ›</Button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Matrix;
